import { mutateGrades } from "@/api/grade/useGradesQuery";
import { useModal } from "@/features/modal/ModalStackManager";
import customAlert from "@/features/ui/alert/alert";
import { handleErrorResponse } from "@/utils/errorMessage";
import { validateFormValues } from "@/utils/validation";
import styled from "@emotion/styled";
import { Box, Button, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { MasterApiGradesPostRequest } from "@sizlcorp/sizl-api-document/dist/models";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

interface GradeCreateFormProps {
  name?: string;
  autoCompleteOnSubmit?: (values: any) => void;
}

export function GradeCreateForm(params: GradeCreateFormProps) {
  const { name, autoCompleteOnSubmit } = params;
  const { closeModal } = useModal();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { mutate: postMutate } = useMutation(
    (params: MasterApiGradesPostRequest) =>
      mutateGrades.create(params).mutationFn(params as MasterApiGradesPostRequest | any),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["grades"]);
        closeModal(form.values);
        customAlert(
          t("message.createSuccess", { item: t("item.grade") }),
          t("common.createSuccess"),
          "green",
        );
      },
      onError: (error: any) => {
        customAlert(
          handleErrorResponse({ error, errorContext: t("item.gradeCode") }),
          t("common.createFail", { item: `${t("item.grade")}` }),
          "red",
        );
      },
    },
  );

  const form = useForm({
    initialValues: {
      code: undefined,
      name: name ?? undefined,
    },
  });

  const onSubmit = async () => {
    try {
      postMutate({
        gradesGetRequest: {
          code: form.values.code!,
          name: name ?? form.values.name!,
        },
      });
    } catch (error) {
      customAlert(
        handleErrorResponse({ error, errorContext: t("item.gradeCode") }),
        t("common.createFail", { item: `${t("item.grade")}` }),
        "red",
      );
    }
  };

  return (
    <Box maw={320} mx="auto">
      <Form>
        <ModalFieldSet>
          <TextInput
            label={t("item.gradeCode")}
            placeholder={t("placeholder.input", { item: t("item.gradeCode") })}
            withAsterisk
            {...form.getInputProps("code")}
          />
          <TextInput
            label={t("item.gradeName")}
            withAsterisk
            width={"100%"}
            placeholder={t("placeholder.input", { item: t("item.gradeName") })}
            {...form.getInputProps("name")}
          />
          <ModalConfirmBtnBox>
            <Button color="gray" onClick={closeModal}>
              {t("common.cancel")}
            </Button>
            <Button
              disabled={validateFormValues({
                pageType: "grade",
                values: form.values,
              })}
              onClick={autoCompleteOnSubmit ? () => autoCompleteOnSubmit(form.values) : onSubmit}
            >
              {t("common.create", { item: t("item.grade") })}
            </Button>
          </ModalConfirmBtnBox>
        </ModalFieldSet>
      </Form>
    </Box>
  );
}

const Form = styled.form`
  width: 100%;
  height: 100%;
`;

const ModalFieldSet = styled.fieldset`
  border: none;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ModalConfirmBtnBox = styled.div`
  padding: 24px 0;
  display: flex;
  justify-content: flex-end;
  gap: 20px;

  // & button {
  //     width: 54px;
  //     height: 40px;
  //     border-radius: 4px;

  //   &.cancel_btn {
  //     background-color: #ffffff;
  //   }
  // }
`;
